/** SUNTRONIX ONE — PDF Style Panel (Configuration Studio).
 *  Tweak fonts, sizes, highlight colour and underline rules for all
 *  quotation / proforma PDF outputs. */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

const CustomerOverrides = ({ presets }) => {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState([]);
  const [target, setTarget] = useState(null);
  const [presetId, setPresetId] = useState("compact");
  const load = () => api.get("/vtiger/quotations/pdf-style/customer-overrides")
    .then(({ data }) => setItems(data.items || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!query.trim() || target) { setHits([]); return; }
    const t = setTimeout(() => api.get("/vtiger/crm-customers", { params: { q: query, limit: 6 } })
      .then(({ data }) => setHits(data.items || [])).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [query, target]);
  const add = async () => {
    if (!target) return;
    try {
      await api.put(`/vtiger/quotations/pdf-style/customer-overrides/${target.id}`, { preset_id: presetId });
      toast.success(`${target.account_name} will always receive the "${presets.find((p) => p.id === presetId)?.name || presetId}" style`);
      setTarget(null); setQuery(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save override"); }
  };
  const remove = async (o) => {
    try {
      await api.put(`/vtiger/quotations/pdf-style/customer-overrides/${o.id}`, { preset_id: null });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not remove"); }
  };
  return (
    <div className="border border-slate-200 rounded-md bg-white px-3 py-2.5" data-testid="pdf-style-customer-overrides">
      <div className="text-[12px] font-medium text-slate-800">Per-customer style</div>
      <div className="text-[10px] text-slate-400 mb-2">These customers always receive their chosen preset on quotations, regardless of the global style.</div>
      <div className="flex gap-1.5 items-start">
        <div className="flex-1 relative">
          {target ? (
            <div className="flex items-center gap-1.5 text-[11px] border border-emerald-200 bg-emerald-50 rounded px-2 py-1.5">
              <span className="font-medium truncate">{target.account_name}</span>
              <button className="ml-auto text-slate-500 text-[10px]" onClick={() => setTarget(null)}>change</button>
            </div>
          ) : (
            <>
              <Input value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder="Search customer…" className="h-8 text-[12px]"
                data-testid="pdf-override-search" />
              {hits.length > 0 && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded shadow-sm max-h-36 overflow-y-auto divide-y">
                  {hits.map((h) => (
                    <button key={h.id} onClick={() => { setTarget(h); setHits([]); }}
                      className="w-full text-left px-2 py-1.5 text-[11px] hover:bg-slate-50 truncate">
                      {h.account_name}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        <select value={presetId} onChange={(e) => setPresetId(e.target.value)}
          className="h-8 border border-slate-200 rounded-md px-1.5 text-[11px] bg-white"
          data-testid="pdf-override-preset">
          {presets.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <Button variant="create" size="sm" className="h-8" onClick={add}
          disabled={!target} data-testid="pdf-override-add">Set</Button>
      </div>
      <div className="mt-2 space-y-1">
        {items.map((o) => (
          <div key={o.id} className="flex items-center gap-2 text-[11px] border border-slate-100 rounded px-2 py-1"
            data-testid={`pdf-override-row-${o.id}`}>
            <span className="truncate font-medium text-slate-700">{o.account_name}</span>
            <span className="text-slate-400">→ {o.preset_name}</span>
            <button onClick={() => remove(o)} title="Remove override"
              data-testid={`pdf-override-del-${o.id}`}
              className="ml-auto text-slate-400 hover:text-rose-600">×</button>
          </div>
        ))}
        {items.length === 0 && <div className="text-[10px] text-slate-400">No per-customer overrides yet.</div>}
      </div>
    </div>
  );
};

const Row = ({ label, hint, children }) => (
  <div className="flex items-center justify-between gap-4 border border-slate-200 rounded-md bg-white px-3 py-2.5">
    <div>
      <div className="text-[12px] font-medium text-slate-800">{label}</div>
      {hint && <div className="text-[10px] text-slate-400">{hint}</div>}
    </div>
    {children}
  </div>
);

export default function PdfStylePanel() {
  const [style, setStyle] = useState(null);
  const [verdanaOk, setVerdanaOk] = useState(true);
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [presets, setPresets] = useState([]);
  const loadPresets = () => api.get("/vtiger/quotations/pdf-style/presets")
    .then(({ data }) => setPresets(data.items || [])).catch(() => {});
  useEffect(() => { loadPresets(); }, []);
  const applyPreset = async (p) => {
    try {
      const { data } = await api.post(`/vtiger/quotations/pdf-style/presets/${p.id}/apply`);
      setStyle(data.style);
      toast.success(`Preset "${data.applied}" applied — now the active PDF style`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not apply preset"); }
  };
  const saveAsPreset = async () => {
    const name = window.prompt("Preset name (e.g. Formal, Compact, Festive):");
    if (!name) return;
    try {
      await api.post("/vtiger/quotations/pdf-style/presets", { name, style });
      toast.success(`Preset "${name}" saved`);
      loadPresets();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save preset"); }
  };
  const removePreset = async (p) => {
    if (!window.confirm(`Delete preset "${p.name}"?`)) return;
    try { await api.delete(`/vtiger/quotations/pdf-style/presets/${p.id}`); loadPresets(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };
  useEffect(() => {
    api.get("/vtiger/quotations/pdf-style/config")
      .then(({ data }) => { setStyle(data.style); setVerdanaOk(data.verdana_available); })
      .catch(() => toast.error("Could not load PDF style"));
  }, []);
  useEffect(() => {
    if (!style) return;
    const t = setTimeout(async () => {
      setPreviewBusy(true);
      try {
        const { data } = await api.post("/vtiger/quotations/pdf-style/preview", style,
          { responseType: "blob" });
        const url = URL.createObjectURL(data);
        setPreviewUrl((old) => { if (old) URL.revokeObjectURL(old); return url; });
      } catch { /* preview unavailable — controls still work */ }
      setPreviewBusy(false);
    }, 600);
    return () => clearTimeout(t);
  }, [style]);
  const set = (k, v) => setStyle((s) => ({ ...s, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/vtiger/quotations/pdf-style/config", style);
      setStyle(data.style);
      toast.success("PDF style saved — applies to every new preview/issue");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };
  if (!style) return <div className="p-6 text-[12px] text-slate-400">Loading…</div>;
  return (
    <div className="p-6" data-testid="pdf-style-panel">
      <div className="text-[10px] uppercase tracking-[0.14em] text-slate-400">Configuration Studio</div>
      <h1 className="text-2xl font-heading font-semibold text-slate-900 mt-0.5">PDF Style</h1>
      <p className="text-[12px] text-slate-500 mt-1">Controls every Quotation / Proforma Invoice PDF. The preview updates live before you save.</p>
      <div className="mt-5 flex gap-6 items-start">
      <div className="w-full max-w-md space-y-2 shrink-0">
        <div className="border border-slate-200 rounded-md bg-white px-3 py-2.5" data-testid="pdf-style-presets">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[12px] font-medium text-slate-800">Style presets</div>
            <button onClick={saveAsPreset} data-testid="pdf-style-save-preset"
              className="text-[10px] font-medium text-emerald-700 border border-emerald-200 rounded px-2 py-0.5 hover:bg-emerald-50">
              Save current as preset
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {presets.map((p) => (
              <span key={p.id} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 pl-2.5 pr-1.5 py-0.5">
                <button onClick={() => applyPreset(p)} data-testid={`pdf-preset-${p.id}`}
                  className="text-[11px] font-medium text-slate-700 hover:text-emerald-700">{p.name}</button>
                {!p.built_in && (
                  <button onClick={() => removePreset(p)} title="Delete preset"
                    data-testid={`pdf-preset-del-${p.id}`}
                    className="text-slate-400 hover:text-rose-600 text-[11px] leading-none">×</button>
                )}
              </span>
            ))}
            {presets.length === 0 && <span className="text-[11px] text-slate-400">Loading presets…</span>}
          </div>
          <div className="text-[10px] text-slate-400 mt-1">One click applies the preset as the active PDF style.</div>
        </div>
        <CustomerOverrides presets={presets} />
        <Row label="Font family" hint={verdanaOk ? "Verdana is the company standard" : "Verdana font file missing — Helvetica used"}>
          <select value={style.font_family} onChange={(e) => set("font_family", e.target.value)}
            className="h-8 border border-slate-200 rounded-md px-2 text-[12px] bg-white" data-testid="pdf-style-font">
            <option>Verdana</option><option>Helvetica</option>
          </select>
        </Row>
        <Row label="Title centered" hint="Quotation / Proforma Invoice heading">
          <Switch checked={!!style.title_center} onCheckedChange={(v) => set("title_center", v)} data-testid="pdf-style-title-center" />
        </Row>
        <Row label="Title underlined">
          <Switch checked={!!style.title_underline} onCheckedChange={(v) => set("title_underline", v)} data-testid="pdf-style-title-underline" />
        </Row>
        <Row label="Title size" hint="10 – 24 pt">
          <Input type="number" min={10} max={24} value={style.title_size}
            onChange={(e) => set("title_size", Number(e.target.value))}
            className="w-20 h-8 text-right" data-testid="pdf-style-title-size" />
        </Row>
        <Row label="Ref# size" hint="9 – 18 pt">
          <Input type="number" min={9} max={18} value={style.ref_size}
            onChange={(e) => set("ref_size", Number(e.target.value))}
            className="w-20 h-8 text-right" data-testid="pdf-style-ref-size" />
        </Row>
        <Row label="Highlight salutation" hint="Dear Mr/Ms … marker">
          <div className="flex items-center gap-2">
            <input type="color" value={style.highlight_color}
              onChange={(e) => set("highlight_color", e.target.value)}
              className="h-8 w-10 border border-slate-200 rounded cursor-pointer"
              data-testid="pdf-style-highlight-color" />
            <Switch checked={!!style.salutation_highlight} onCheckedChange={(v) => set("salutation_highlight", v)} data-testid="pdf-style-salutation" />
          </div>
        </Row>
        <Row label="Underline Kind Attn name">
          <Switch checked={!!style.underline_kind_attn} onCheckedChange={(v) => set("underline_kind_attn", v)} data-testid="pdf-style-kind-attn" />
        </Row>
        <Row label="Underline address locality" hint="City / State / Country / PIN lines">
          <Switch checked={!!style.underline_address_locality} onCheckedChange={(v) => set("underline_address_locality", v)} data-testid="pdf-style-address" />
        </Row>
        <Row label="Header alignment" hint="Letterhead artwork position">
          <select value={style.header_align} onChange={(e) => set("header_align", e.target.value)}
            className="h-8 border border-slate-200 rounded-md px-2 text-[12px] bg-white" data-testid="pdf-style-header-align">
            <option value="center">Centered</option><option value="left">Left</option>
          </select>
        </Row>
      <div className="pt-2 flex gap-2">
        <Button onClick={save} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800"
          data-testid="pdf-style-save">{busy ? "Saving…" : "Save style"}</Button>
      </div>
      </div>
      <div className="flex-1 min-w-0 border border-slate-200 rounded-xl overflow-hidden bg-slate-50"
        data-testid="pdf-style-preview-pane">
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 bg-white">
          <span className="text-[11px] uppercase tracking-wide text-slate-500">Live preview — latest quotation</span>
          <span className="flex items-center gap-3">
            {previewBusy && <span className="text-[10px] text-slate-400">Rendering…</span>}
            {previewUrl && (
              <a href={previewUrl} target="_blank" rel="noreferrer"
                className="text-[10px] text-emerald-700 underline" data-testid="pdf-style-preview-open">
                Open in new tab
              </a>
            )}
          </span>
        </div>
        {previewUrl ? (
          <iframe title="PDF style preview" src={previewUrl}
            className="w-full h-[72vh] bg-white" data-testid="pdf-style-preview-frame" />
        ) : (
          <div className="h-[72vh] flex items-center justify-center text-[12px] text-slate-400">
            {previewBusy ? "Rendering preview…" : "Preview unavailable"}
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
